import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "content", 
    "modal", 
    "selectedText", 
    "form",
    "startField",
    "endField", 
    "textField",
    "closeButton",
    "cancelButton",
    "textarea"
  ]
  
  static values = {
    submitUrl: String
  }

  connect() {
    this.currentSelection = { start: null, end: null, text: null }
    this.setupEventListeners()
  }

  setupEventListeners() {
    this.contentTarget.addEventListener('mouseup', this.handleTextSelection.bind(this))
    this.closeButtonTarget.addEventListener('click', this.hideModal.bind(this))
    this.cancelButtonTarget.addEventListener('click', this.hideModal.bind(this))
    this.modalTarget.addEventListener('click', this.handleModalClick.bind(this))
    this.formTarget.addEventListener('submit', this.handleFormSubmit.bind(this))
  }

  handleTextSelection() {
    const selection = window.getSelection()
    const selectedText = selection.toString().trim()
    
    if (selectedText.length > 0) {
      const range = selection.getRangeAt(0)
      const preSelectionRange = range.cloneRange()
      preSelectionRange.selectNodeContents(this.contentTarget)
      preSelectionRange.setEnd(range.startContainer, range.startOffset)
      const selectionStart = preSelectionRange.toString().length
      const selectionEnd = selectionStart + selectedText.length
      
      this.showModal(selectedText, selectionStart, selectionEnd)
    }
  }

  showModal(selectedText, selectionStart, selectionEnd) {
    this.selectedTextTarget.textContent = selectedText
    
    this.currentSelection = {
      start: selectionStart,
      end: selectionEnd,
      text: selectedText
    }
    
    this.startFieldTarget.value = selectionStart
    this.endFieldTarget.value = selectionEnd
    this.textFieldTarget.value = selectedText
    
    this.modalTarget.classList.remove('hidden')
    
    setTimeout(() => {
      this.textareaTarget.focus()
    }, 100)
  }

  hideModal() {
    this.modalTarget.classList.add('hidden')
    this.textareaTarget.value = ''
    window.getSelection().removeAllRanges()
    
    this.currentSelection = { start: null, end: null, text: null }
  }

  handleModalClick(event) {
    if (event.target === this.modalTarget) {
      this.hideModal()
    }
  }

  async handleFormSubmit(event) {
    event.preventDefault()
    
    const submitButton = this.formTarget.querySelector('input[type="submit"]')
    const originalText = submitButton.value
    submitButton.value = 'Adding...'
    submitButton.disabled = true
    
    try {
      const response = await fetch(this.submitUrlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          document_comment: {
            content: this.textareaTarget.value,
            selection_start: this.currentSelection.start,
            selection_end: this.currentSelection.end,
            selection_text: this.currentSelection.text
          }
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || errorData.errors?.join(', ') || `HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      
      if (data.id) {
        this.hideModal()
        window.location.reload()
      } else {
        throw new Error(data.error || data.errors?.join(', ') || 'Unknown error')
      }
    } catch (error) {
      alert('Error adding comment: ' + error.message)
    } finally {
      submitButton.value = originalText
      submitButton.disabled = false
    }
  }

  resolveComment(event) {
    const button = event.currentTarget
    const commentId = button.dataset.commentId
    const recordId = button.dataset.recordId
    const recordType = button.dataset.recordType
    
    if (confirm('Are you sure you want to resolve this comment?')) {
      button.disabled = true
      button.textContent = 'Resolving...'
      
      const resolveUrl = this.buildResolveUrl(recordType, recordId, commentId)
      
      fetch(resolveUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        }
      })
      .then(response => response.json())
      .then(data => {
        if (data.id) {
          const statusContainer = button.parentElement
          statusContainer.innerHTML = '<span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Resolved</span>'
          this.showSuccessMessage('Comment resolved successfully')
        } else {
          throw new Error('Failed to resolve comment')
        }
      })
      .catch(error => {
        console.error('Error resolving comment:', error)
        button.disabled = false
        button.textContent = 'Resolve'
        this.showErrorMessage('Failed to resolve comment')
      })
    }
  }

  buildResolveUrl(recordType, recordId, commentId) {
    switch(recordType) {
      case 'engagement':
        return `/inloops/${this.getInloopId()}/engagements/${recordId}/document_comments/${commentId}/resolve`
      case 'inloop':
        return `/inloops/${recordId}/document_comments/${commentId}/resolve`
      case 'contract':
        return `/contracts/${recordId}/document_comments/${commentId}/resolve`
      default:
        throw new Error(`Unknown record type: ${recordType}`)
    }
  }

  getInloopId() {
    const pathParts = window.location.pathname.split('/')
    const inloopIndex = pathParts.indexOf('inloops')
    return inloopIndex !== -1 ? pathParts[inloopIndex + 1] : null
  }

  showSuccessMessage(message) {
    this.showMessage(message, 'bg-green-100 border-green-400 text-green-700')
  }

  showErrorMessage(message) {
    this.showMessage(message, 'bg-red-100 border-red-400 text-red-700')
  }

  showMessage(text, classes) {
    const message = document.createElement('div')
    message.className = `fixed top-4 right-4 ${classes} px-4 py-3 rounded z-50`
    message.textContent = text
    document.body.appendChild(message)
    
    setTimeout(() => {
      message.remove()
    }, 3000)
  }
}